/**
 * Re-export the LayoutItem type from react-grid-layout v2.
 * Using the library's type directly ensures compatibility.
 */
export type { LayoutItem, Layout } from 'react-grid-layout';
import type { ChartType } from '../../api/types';

/**
 * Default size & constraints for a given chart type.
 * Used when a chart has no saved layout position.
 */
export interface ChartLayoutDefaults {
  w: number;
  h: number;
  minH: number;
  maxH: number;
  minW: number;
}

/**
 * Grid configuration constants
 *
 * 12-column grid — industry standard (Grafana, Metabase, Bootstrap).
 * At ~1500px viewport, each column is ~115px → smooth resize snapping.
 * Row height of 20px means height snaps in 20px increments.
 */
export const GRID_COLS = 12;
export const GRID_ROW_HEIGHT = 20;
export const GRID_MARGIN: [number, number] = [16, 16];
export const GRID_CONTAINER_PADDING: [number, number] = [0, 0];

/**
 * Default layout constraints per chart type.
 *
 * All values are in 12-col grid units.
 *
 * Width reference (12-col):
 *   3 = 25%  |  4 = 33%  |  6 = 50%  |  9 = 75%  |  12 = 100%
 *
 * Height reference (×20px):
 *   4 = 80px  |  8 = 160px  |  10 = 200px  |  13 = 260px  |  20 = 400px
 */
export const CHART_LAYOUT_DEFAULTS: Partial<Record<ChartType, ChartLayoutDefaults>> & Record<string, ChartLayoutDefaults> = {
  // KPI: compact cards — 4 per row
  kpi:         { w: 3,  h: 4,  minH: 4,  maxH: 8,  minW: 2 },
  // KPI wide: wider card — 3 per row
  kpi_wide:    { w: 4,  h: 4,  minH: 4,  maxH: 8,  minW: 3 },

  // Standard charts — half-width default
  line:        { w: 6,  h: 13, minH: 10, maxH: 40, minW: 3 },
  bar:         { w: 6,  h: 13, minH: 10, maxH: 40, minW: 3 },
  area:        { w: 6,  h: 13, minH: 10, maxH: 40, minW: 3 },
  scatter:     { w: 6,  h: 13, minH: 10, maxH: 40, minW: 3 },

  // Pie/gauge/funnel — half-width default
  pie:         { w: 6,  h: 13, minH: 10, maxH: 40, minW: 3 },
  gauge:       { w: 6,  h: 13, minH: 10, maxH: 40, minW: 3 },
  funnel:      { w: 6,  h: 13, minH: 10, maxH: 40, minW: 3 },

  // Tables/heatmaps — half-width default
  table:       { w: 6,  h: 13, minH: 10, maxH: 40, minW: 3 },
  heatmap:     { w: 6,  h: 13, minH: 10, maxH: 40, minW: 3 },
  cohort:      { w: 6,  h: 13, minH: 10, maxH: 40, minW: 3 },

  // Other chart types
  histogram:   { w: 6,  h: 13, minH: 10, maxH: 40, minW: 3 },
  map:         { w: 6,  h: 13, minH: 10, maxH: 40, minW: 3 },
  box_plot:    { w: 6,  h: 13, minH: 10, maxH: 40, minW: 3 },
  word_cloud:  { w: 6,  h: 13, minH: 10, maxH: 40, minW: 3 },
};

/** Fallback defaults for unknown chart types */
export const DEFAULT_CHART_LAYOUT: ChartLayoutDefaults = {
  w: 6,
  h: 13,
  minH: 10,
  maxH: 40,
  minW: 3,
};